import { useMemo, useCallback } from 'react'
import { ArrowLeftIcon, EyeIcon, DownloadSimpleIcon } from '@phosphor-icons/react'
import {
    ScoopText,
    ScoopButton,
    ScoopChip,
    ScoopTable,
    ScoopColumn,
    ScoopRow,
    ScoopAccordion,
    ScoopCenter,
    ScoopSpinner,
    ScoopAlert,
} from '../../../../ui'
import { MarkdownSection } from '../InvestigationReport/components/MarkdownSection/MarkdownSection'
import { ReportBreadcrumb } from '../ReportBreadcrumb/ReportBreadcrumb'
import { BlufStatement } from '../BlufStatement/BlufStatement'
import { StatStrip } from '../StatStrip/StatStrip'
import { SeverityTag } from '../SeverityTag/SeverityTag'
import { SeverityDistributionBar } from '../SeverityDistributionBar/SeverityDistributionBar'
import { priorityColor } from '../../utils/indicatorColors'
import {
    actionMatchesHierarchyNode,
    buildHierarchyTree,
    flattenHierarchyTree,
    getActionScopeLabel,
    getDescendantEntities,
    getEntityDisplayId,
    getEntityHierarchyPathLabel,
    getEntityId,
    getEntityKeyFinding,
    getEntityTypeLabel,
    getHierarchyEntries,
    isFlaggedEntity,
    normalizeSeverity,
    severityRank,
} from '../../utils/hierarchyUtils'
import { severityLabel } from '../../utils/severity'
import { formatDateTime } from '../../utils/formatDate'
import styles from './RollupReport.module.scss'

const STATUS_COLORS = {
    queued: 'default',
    running: 'info',
    succeeded: 'success',
    failed: 'error',
    partial_failed: 'warning',
    cancelled: 'default',
}

const SUMMARY_HEADING_PATTERN =
    /^(#{1,6}\s*)?(executive summary|summary|narrative|key findings|findings)$/i

const extractSummarySentence = (summary) => {
    const synthesisJson = summary?.synthesisJson || {}
    const bluf = summary?.bluf?.trim?.() || synthesisJson.bluf?.trim?.()
    if (bluf) return bluf

    const narrative = summary?.narrative?.trim?.() || synthesisJson.narrative?.trim?.()
    if (!narrative) return ''

    const text = narrative
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line && !SUMMARY_HEADING_PATTERN.test(line))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim()

    if (!text) return ''

    const sentence = text.match(/^.*?[.!?](?=\s|$)/)
    return sentence?.[0]?.trim() || text
}

const extractSummaryAnalysis = (summary) => {
    const synthesisJson = summary?.synthesisJson || {}
    return (
        synthesisJson.narrative?.trim?.() ||
        summary?.narrative?.trim?.() ||
        synthesisJson.analysis?.trim?.() ||
        summary?.analysis?.trim?.() ||
        ''
    )
}

const normalizeNodeKey = (value) =>
    String(value || '')
        .trim()
        .toLowerCase()

const selectHierarchySummary = (report, dateNode) => {
    if (!report || dateNode?.nodeType !== 'hierarchy_node') return report?.summary

    const summaries = Array.isArray(report.hierarchySummaries) ? report.hierarchySummaries : []
    const selectedLevel = normalizeNodeKey(dateNode.levelName)
    const selectedNode = normalizeNodeKey(dateNode.nodeId)

    return (
        summaries.find(
            (summary) =>
                normalizeNodeKey(summary?.levelName) === selectedLevel &&
                normalizeNodeKey(summary?.nodeId) === selectedNode
        ) || report.summary
    )
}

const collectEntityChildren = (node) => {
    if (!node) return []
    if (node.nodeType === 'hierarchy_node') return getDescendantEntities(node)
    return (node.children || []).flatMap((child) => {
        if (child.nodeType === 'entity') return [child]
        if (child.nodeType === 'hierarchy_node') return getDescendantEntities(child)
        return []
    })
}

const inferHierarchyLevels = (entities) => {
    const levels = []
    for (const entity of entities) {
        for (const entry of getHierarchyEntries(entity)) {
            if (!levels.includes(entry.levelName)) levels.push(entry.levelName)
        }
    }
    return levels
}

const sortEntities = (entities) =>
    [...entities].sort((a, b) => {
        const bySeverity = severityRank(a.severity) - severityRank(b.severity)
        if (bySeverity !== 0) return bySeverity
        return String(getEntityDisplayId(a)).localeCompare(
            String(getEntityDisplayId(b)),
            undefined,
            {
                numeric: true,
            }
        )
    })

/**
 * Engine-authoritative Scanned/Flagged counts (audit F7). For a whole-run view
 * these come from report.statistics; for a focused hierarchy node from that
 * node's LevelSynthesisResult (totalEntityCount/flaggedEntityCount). Returns
 * null when the report carries no counts (legacy runs) — caller falls back to
 * client-derived numbers.
 */
const selectAuthoritativeCounts = (report, dateNode) => {
    if (!report) return null

    if (dateNode?.nodeType === 'hierarchy_node') {
        const summaries = Array.isArray(report.hierarchySummaries) ? report.hierarchySummaries : []
        const selectedLevel = normalizeNodeKey(dateNode.levelName)
        const selectedNode = normalizeNodeKey(dateNode.nodeId)
        const match = summaries.find(
            (summary) =>
                normalizeNodeKey(summary?.levelName) === selectedLevel &&
                normalizeNodeKey(summary?.nodeId) === selectedNode
        )
        if (match && (match.totalEntityCount != null || match.flaggedEntityCount != null)) {
            return {
                scanned: match.totalEntityCount ?? null,
                flagged: match.flaggedEntityCount ?? null,
                source: `${dateNode.levelName} rollup`,
            }
        }
        return null
    }

    const statistics = report.statistics
    if (statistics && (statistics.totalEntities != null || statistics.flaggedEntities != null)) {
        return {
            scanned: statistics.totalEntities ?? null,
            flagged: statistics.flaggedEntities ?? null,
            source: 'run statistics',
        }
    }
    return null
}

/**
 * RollupReport — hierarchy-aware screening summary view for a cycle/date or hierarchy node.
 */
const DEFAULT_TERMINOLOGY = { label: (key, fallback) => fallback }

export const RollupReport = ({
    dateNode,
    report,
    loadingReport,
    reportError,
    actionCandidates = [],
    onViewEntity,
    onBack,
    // Customer-language labels (metadata.terminology via useTerminology);
    // neutral fallbacks when the workspace defines none.
    terminology = DEFAULT_TERMINOLOGY,
    breadcrumbs,
}) => {
    const { planName, dateLabel, runs = [] } = dateNode
    const defaultRunPath = dateNode.runPath || runs.find((run) => run.runPath)?.runPath
    const isFocusedNode = dateNode.nodeType === 'hierarchy_node'
    const activeSummary = useMemo(
        () => selectHierarchySummary(report, dateNode),
        [dateNode, report]
    )
    const summarySentence = extractSummarySentence(activeSummary)
    const summaryAnalysis = extractSummaryAnalysis(activeSummary)
    const analysisTitle =
        isFocusedNode && dateNode.levelName ? `${dateNode.levelName} Analysis` : 'Analysis'

    const reportEntities = useMemo(() => report?.entitySummaries || [], [report])
    const fallbackEntities = useMemo(() => collectEntityChildren(dateNode), [dateNode])

    const hierarchyLevels = useMemo(() => {
        if (Array.isArray(report?.hierarchyLevels) && report.hierarchyLevels.length > 0) {
            return report.hierarchyLevels
        }
        if (Array.isArray(dateNode.hierarchyLevels) && dateNode.hierarchyLevels.length > 0) {
            return dateNode.hierarchyLevels
        }
        return inferHierarchyLevels(reportEntities.length > 0 ? reportEntities : fallbackEntities)
    }, [dateNode.hierarchyLevels, fallbackEntities, report?.hierarchyLevels, reportEntities])

    const visibleEntities = useMemo(() => {
        const source = reportEntities.length > 0 ? reportEntities : fallbackEntities
        const scoped =
            isFocusedNode && dateNode.levelName && dateNode.nodeId
                ? source.filter(
                      (entity) => entity?.hierarchy?.[dateNode.levelName] === dateNode.nodeId
                  )
                : source

        return sortEntities(
            scoped.map((entity) => ({
                ...entity,
                runPath: entity.runPath || defaultRunPath,
                keyFinding: getEntityKeyFinding(entity),
            }))
        )
    }, [
        dateNode.levelName,
        dateNode.nodeId,
        defaultRunPath,
        fallbackEntities,
        isFocusedNode,
        reportEntities,
    ])

    const hierarchyTree = useMemo(
        () => buildHierarchyTree(visibleEntities, hierarchyLevels),
        [hierarchyLevels, visibleEntities]
    )

    const hierarchyRows = useMemo(() => flattenHierarchyTree(hierarchyTree), [hierarchyTree])

    const entityTypeLabel = useMemo(
        () => getEntityTypeLabel(report, visibleEntities[0] || fallbackEntities[0]),
        [fallbackEntities, report, visibleEntities]
    )

    const summaryStats = useMemo(() => {
        // Severity counts are only derivable from the entity list (the engine
        // publishes labelDistribution, not per-severity totals).
        //
        // Critical is counted on its own. While `critical` was an alias of
        // Severe this band was accurate, but under six tiers a `rank <= severe`
        // band would tally Critical stores under a stat LABELLED "Severe" —
        // understating urgency in the one number a leader glances at.
        const flagged = visibleEntities.filter(isFlaggedEntity)
        const criticalCount = flagged.filter(
            (entity) => severityRank(entity.severity) <= severityRank('critical')
        ).length
        const severeCount = flagged.filter(
            (entity) =>
                severityRank(entity.severity) > severityRank('critical') &&
                severityRank(entity.severity) <= severityRank('severe')
        ).length
        const moderateCount = flagged.filter(
            (entity) =>
                severityRank(entity.severity) > severityRank('severe') &&
                severityRank(entity.severity) <= severityRank('moderate')
        ).length

        const derivedScanned =
            visibleEntities.length > 0
                ? visibleEntities.length
                : runs.reduce((sum, r) => sum + (r.totalTasks || 0), 0)
        const derived = { scanned: derivedScanned, flagged: flagged.length }

        // Scanned/Flagged prefer the engine's own numbers (audit F7) so this
        // view can agree with the DOCX and Portal by construction. When derived
        // and authoritative disagree, we still show the engine's numbers but
        // surface the mismatch instead of hiding it.
        const authoritative = selectAuthoritativeCounts(report, dateNode)
        const countMismatch =
            authoritative &&
            ((authoritative.scanned != null && authoritative.scanned !== derived.scanned) ||
                (authoritative.flagged != null && authoritative.flagged !== derived.flagged))
                ? { derived, authoritative }
                : null

        return {
            scanned: authoritative?.scanned ?? derived.scanned,
            flagged: authoritative?.flagged ?? derived.flagged,
            critical: criticalCount,
            severe: severeCount,
            moderate: moderateCount,
            countMismatch,
        }
    }, [dateNode, report, runs, visibleEntities])

    const scopedActions = useMemo(() => {
        if (!isFocusedNode) {
            const entityIds = new Set(visibleEntities.map(getEntityId))
            return actionCandidates.filter(
                (candidate) =>
                    candidate.scopeType === 'portfolio' ||
                    (candidate.scopeEntityId && entityIds.has(candidate.scopeEntityId)) ||
                    (candidate.scopeType === 'hierarchy_node' &&
                        visibleEntities.some(
                            (entity) =>
                                entity?.hierarchy?.[candidate.scopeLevelName] ===
                                candidate.scopeNodeId
                        ))
            )
        }
        return actionCandidates.filter((candidate) =>
            actionMatchesHierarchyNode(candidate, dateNode)
        )
    }, [actionCandidates, dateNode, isFocusedNode, visibleEntities])

    const tableColumns = useMemo(
        () => [
            { field: 'entityId', header: entityTypeLabel },
            {
                field: 'severity',
                header: 'Severity',
                render: ({ value }) => <SeverityTag severity={value} />,
            },
            { field: 'hierarchyPath', header: 'Hierarchy' },
            { field: 'keyFinding', header: 'Key Finding' },
            {
                field: 'actions',
                header: '',
                render: ({ row }) => (
                    <ScoopButton
                        variant="grey"
                        size="xs"
                        onClick={() => onViewEntity?.(row.entity || row)}
                        disabled={!row.runPath}
                    >
                        <EyeIcon size={14} />
                        View
                    </ScoopButton>
                ),
                width: 100,
            },
        ],
        [entityTypeLabel, onViewEntity]
    )

    const tableRows = useMemo(
        () =>
            visibleEntities.map((entity) => ({
                entity,
                entityId: getEntityDisplayId(entity),
                rawEntityId: getEntityId(entity),
                severity: normalizeSeverity(entity.severity),
                hierarchyPath: getEntityHierarchyPathLabel(entity, hierarchyLevels) || '-',
                keyFinding: getEntityKeyFinding(entity) || '-',
                runPath: entity.runPath,
                runId: entity.runId,
            })),
        [hierarchyLevels, visibleEntities]
    )

    const handleExportCsv = useCallback(() => {
        const headers = [entityTypeLabel, 'Severity', 'Key Finding']
        const csvRows = [headers.join(',')]
        for (const row of tableRows) {
            const escapedFinding = `"${(row.keyFinding || '').replace(/"/g, '""')}"`
            csvRows.push(`${row.entityId},${severityLabel(row.severity)},${escapedFinding}`)
        }
        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `rollup_${planName || 'report'}_${dateLabel || 'unknown'}.csv`
        link.click()
        URL.revokeObjectURL(url)
    }, [tableRows, planName, dateLabel, entityTypeLabel])

    const reportState = loadingReport ? (
        <ScoopCenter className={styles.inlineState}>
            <ScoopSpinner size={20} />
            <ScoopText className={styles.inlineStateText}>Loading hierarchy artifacts</ScoopText>
        </ScoopCenter>
    ) : reportError ? (
        <ScoopAlert severity="error" className={styles.stateAlert}>
            Unable to load hierarchy artifacts: {reportError.message}
        </ScoopAlert>
    ) : null

    return (
        <div className={styles.container}>
            {breadcrumbs?.length > 1 && (
                <div className={styles.breadcrumbRow}>
                    <ReportBreadcrumb crumbs={breadcrumbs} />
                </div>
            )}
            <div className={styles.header}>
                <ScoopButton variant="grey" size="sm" onClick={onBack}>
                    <ArrowLeftIcon size={14} />
                    Back
                </ScoopButton>
            </div>

            <div className={styles.body}>
                <div className={styles.titleSection}>
                    <ScoopText className={styles.planName}>
                        {isFocusedNode ? dateNode.nodeId : planName}
                    </ScoopText>
                    <ScoopText className={styles.dateLabel}>{dateLabel}</ScoopText>
                    {isFocusedNode && (
                        <ScoopText className={styles.focusMeta}>
                            {dateNode.levelName} · {planName}
                        </ScoopText>
                    )}
                </div>

                {reportState}

                {summarySentence && (
                    <div className={styles.blufZone}>
                        <BlufStatement>{summarySentence}</BlufStatement>
                    </div>
                )}

                {summaryAnalysis && (
                    <div className={styles.analysisSection}>
                        <ScoopAccordion defaultExpanded>
                            <ScoopAccordion.Summary>
                                <ScoopText className={styles.sectionTitle}>
                                    {analysisTitle}
                                </ScoopText>
                            </ScoopAccordion.Summary>
                            <ScoopAccordion.Details>
                                <div className={styles.analysisBody}>
                                    <MarkdownSection content={summaryAnalysis} />
                                </div>
                            </ScoopAccordion.Details>
                        </ScoopAccordion>
                    </div>
                )}

                {summaryStats.countMismatch && (
                    <ScoopAlert severity="warning" className={styles.stateAlert}>
                        Count reconciliation: this view derived{' '}
                        {summaryStats.countMismatch.derived.scanned} scanned /{' '}
                        {summaryStats.countMismatch.derived.flagged} flagged from the entity list,
                        but the engine&apos;s {summaryStats.countMismatch.authoritative.source}{' '}
                        reports {summaryStats.countMismatch.authoritative.scanned} /{' '}
                        {summaryStats.countMismatch.authoritative.flagged}. Showing engine counts —
                        the entity list below may be incomplete.
                    </ScoopAlert>
                )}

                <StatStrip
                    className={styles.statStripZone}
                    items={[
                        {
                            value: summaryStats.scanned,
                            label: terminology.label('screened', 'Scanned'),
                        },
                        {
                            value: summaryStats.flagged,
                            label: terminology.label('flagged', 'Flagged'),
                            tone: summaryStats.flagged > 0 ? 'warning' : undefined,
                        },
                        // Critical appears only when there is one, so a run
                        // without any keeps the familiar five-stat strip.
                        ...(summaryStats.critical > 0
                            ? [
                                  {
                                      value: summaryStats.critical,
                                      label: 'Critical',
                                      tone: 'critical',
                                  },
                              ]
                            : []),
                        {
                            value: summaryStats.severe,
                            label: 'Severe',
                            tone: summaryStats.severe > 0 ? 'error' : undefined,
                        },
                        {
                            value: summaryStats.moderate,
                            label: 'Moderate',
                            tone: summaryStats.moderate > 0 ? 'warning' : undefined,
                        },
                        { value: scopedActions.length, label: 'Actions' },
                    ]}
                />

                {visibleEntities.length > 0 && (
                    <div className={styles.distributionZone}>
                        <SeverityDistributionBar
                            entities={visibleEntities}
                            entitiesNoun={terminology.label('entities', 'Entities').toLowerCase()}
                        />
                    </div>
                )}

                {hierarchyRows.length > 0 && (
                    <div className={styles.section}>
                        <ScoopText className={styles.sectionTitle}>Hierarchy</ScoopText>
                        <div className={styles.hierarchyList}>
                            {hierarchyRows.map((node) => (
                                <div
                                    key={node.id}
                                    className={styles.hierarchyRow}
                                    style={{ '--depth': Math.min(node.depth, 3) }}
                                >
                                    <ScoopColumn className={styles.hierarchyLabelBlock}>
                                        <ScoopText className={styles.hierarchyNodeName}>
                                            {node.nodeId}
                                        </ScoopText>
                                        <ScoopText className={styles.hierarchyNodeMeta}>
                                            {node.levelName}
                                        </ScoopText>
                                    </ScoopColumn>
                                    <ScoopRow className={styles.hierarchyMetrics}>
                                        <span>{node.entityCount} entities</span>
                                        <span>{node.flaggedCount} flagged</span>
                                        <span>{node.severeCount} severe</span>
                                    </ScoopRow>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {scopedActions.length > 0 && (
                    <div className={styles.section}>
                        <ScoopText className={styles.sectionTitle}>
                            Active Actions in View
                        </ScoopText>
                        <div className={styles.actionSummaryList}>
                            {scopedActions.slice(0, 6).map((candidate) => (
                                <div
                                    key={candidate.candidateFingerprint}
                                    className={styles.actionSummaryRow}
                                >
                                    <ScoopText
                                        className={`${styles.actionPriority} ${
                                            styles[
                                                `priority${priorityColor(candidate.priority || 'MEDIUM')}`
                                            ]
                                        }`}
                                    >
                                        {candidate.priority || 'MEDIUM'}
                                    </ScoopText>
                                    <ScoopColumn className={styles.actionSummaryText}>
                                        <ScoopText className={styles.actionTitle}>
                                            {candidate.title || 'Untitled action'}
                                        </ScoopText>
                                        <ScoopText className={styles.actionScope}>
                                            {getActionScopeLabel(candidate)}
                                        </ScoopText>
                                    </ScoopColumn>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {tableRows.length > 0 && (
                    <div className={styles.section}>
                        <ScoopRow className={styles.sectionHeaderRow}>
                            <ScoopText className={styles.sectionTitle}>
                                {terminology.label('entities', 'Entities')}
                            </ScoopText>
                            <ScoopButton
                                variant="grey"
                                size="xs"
                                onClick={handleExportCsv}
                                disabled={tableRows.length === 0}
                            >
                                <DownloadSimpleIcon size={14} />
                                Export CSV
                            </ScoopButton>
                        </ScoopRow>
                        <ScoopTable columns={tableColumns} rows={tableRows} size="sm" />
                    </div>
                )}

                {tableRows.length === 0 && (
                    <div className={styles.section}>
                        <ScoopText className={styles.noFlagged}>
                            No entities were found for this view.
                        </ScoopText>
                    </div>
                )}

                {runs.length > 0 && (
                    <div className={styles.section}>
                        <ScoopText className={styles.sectionTitle}>Runs on this date</ScoopText>
                        <ScoopRow className={styles.runChips}>
                            {runs.map((run) => (
                                <ScoopChip
                                    key={run.runId}
                                    label={`${formatDateTime(run.startedAt) || run.runId.slice(0, 8)} — ${run.status}`}
                                    color={STATUS_COLORS[run.status] || 'default'}
                                    size="sm"
                                />
                            ))}
                        </ScoopRow>
                    </div>
                )}
            </div>
        </div>
    )
}
